import React, { useMemo } from 'react'
import { Application } from '../../types/sponsorships/campaigns'
import { Link } from 'react-router-dom';
import { copyToClipboard, formatAmount, maskString, textToTitleCase } from '../../utils/helpers';
import moment from 'moment';

const ApplicationCard: React.FC<{ application: Application, applied?: boolean }> = ({ application, applied = false }) => {

    const isCompleted = useMemo(() => {
        if (!application.active_milestone) {
            const completedMilestones = (application.milestones ?? []).filter((m: any) => m.ended_at);
            return completedMilestones.length === (application.milestones ?? []).length;
        }
        return false;
    }, [application])

    const lastMilestone = useMemo(() => {
        if (!application) return null;
        const milestones = [...(application.milestones ?? [])].sort((a, b) => a.sort_order - b.sort_order);
        if (milestones.length) {
            return milestones[milestones.length - 1];
        }
        return null;
    }, [application])

    const payments = useMemo(() => {
        return Array.isArray(lastMilestone?.payments) ? lastMilestone?.payments : [];
    }, [lastMilestone])


    const getPlannedPayoutDate = () => {
        if (!lastMilestone?.planned_payout_date) return "-";
        const momentObj = moment(lastMilestone.planned_payout_date);
        const isToday = momentObj.isSame(moment(), 'day');
        if (isToday) return "today";
        const isTomorrow = momentObj.isSame(moment().add(1, 'day'), 'day');
        if (isTomorrow) return "tomorrow";

        return "on " + momentObj.format("MMMM Do YYYY");

    }

    const currency = useMemo(() => {
        return payments?.[0]?.currency ?? null;
    }, [payments])

    const pendingPayoutAmount = useMemo(() => {
        if (!payments) return 0;
        const amount = payments.reduce((sum: number, payment: any) => {
            if (payment.status === 'pending') {
                return sum + Number(payment.amount);
            }
            return sum;
        }, 0);
        return amount;
    }, [payments])

    return (
        <div className='app-card-wrapper position-relative overflow-hidden'>
            {
                applied &&
                <div className={`approval-status-badge ${application.status}`}>{textToTitleCase(application.status)}</div>
            }
            <div className="app-card p-075 box-shadow-none">
                <div className="d-flex align-items-center gap-3">
                    <Link to={`/campaign/${application?.campaign?.uuid}`}>
                        <div className="application-card-img" style={{ backgroundImage: `url(${application.campaign?.hero_img_url})` }}></div>
                    </Link>
                    <div className="d-flex flex-column gap-1 w-100">
                        <span className="fs-14 text-dark fw-700 text-truncate vw-60">{application.campaign?.name}</span>
                        <span className="fs-12 text-muted fw-400 text-truncate vw-60">{application.campaign?.brand?.name}</span>
                    </div>
                </div>
                {
                    !applied &&
                    <Link to={{ pathname: isCompleted ? `/collaborations/${application?.campaign?.uuid}` : `/collaborations/${application?.campaign?.uuid}/milestone/${application?.active_milestone?.id}` }} className="btn btn-outline-primary btn-sm mt-3 w-100">View Milestone</Link>
                }
                {
                    applied &&
                    <div className="text-muted mt-3" style={{ textTransform: "uppercase" }}> <span>applied {application.applied_on}</span></div>
                }
            </div>
            {
                (application.hub && !isCompleted) &&
                <div className='application-additional-info'>
                    <div className="">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex flex-column gap-1">
                                <span className="hub-contact">
                                    {application.hub?.name} | {maskString(application.hub?.mobile ?? '', 3, 7)} <button className='unset-all' onClick={() => copyToClipboard(application.hub?.mobile ?? "", "Contact number copied to your clipboard!")}><i className="ms-1 far fa-copy text-primary"></i></button>
                                </span>
                                <span className="hub-heading">
                                    Campaign Manager
                                </span>
                            </div>
                            <a href={`https://api.whatsapp.com/send?phone=${application.hub?.mobile ?? ''}&text=Hi`} rel="noreferrer" target="_blank" className='text-success fs-5'><i className='fab fa-whatsapp'></i></a>
                        </div>
                    </div>
                </div>
            }
            {
                isCompleted && (pendingPayoutAmount > 0) && lastMilestone?.planned_payout_date &&
                <div className='application-additional-info'>
                    <div className="">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex flex-column gap-1">
                                <span className="hub-heading">
                                    Your payment of {formatAmount(pendingPayoutAmount, currency?.code, currency?.symbol, currency?.locale)} will be paid {getPlannedPayoutDate()}.
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            }
        </div>
    )
}

export default ApplicationCard